import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useGetAdminByIdQuery, useEditAdminByIdMutation } from "../../services";
import { adminRoutes } from "../../routers/AdminRouters";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import CountryPhoneInput, { getAllCountryCodes } from "../../components/CountryPhoneInput";



const AdminEdit: React.FC = () => {
  const { theme } = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useGetAdminByIdQuery(id ?? "", {
    skip: !id,
  });

  const [editAdmin, { isLoading: isUpdating }] = useEditAdminByIdMutation();

  const [form, setForm] = useState<{
    name: string;
    email: string;
    countryCode: string;
    phone: string;
    password?: string;

    role?: string;
    status?: string;
  }>({
    name: "",
    email: "",
    countryCode: "+91",
    phone: "",
    password: "",
    role: "",
    status: "Active",
  });


  // inline errors
  const [errors, setErrors] = useState<{
    email?: string;
    phone?: string;
    password?: string;
  }>({});
  const [createPassword, setCreatePassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const emailRegex = /^\S+@\S+\.\S+$/;

  useEffect(() => {
    if (data) {
      const phoneValue = (data as any)?.phone;
      const fullPhone = phoneValue !== undefined && phoneValue !== null ? String(phoneValue) : "";
      let countryCode = "+91";
      let phoneNumber = fullPhone;


      const dynamicCountryCodes = getAllCountryCodes();

      // Extract country code from full phone number
      if (fullPhone.startsWith("+")) {
        const found = dynamicCountryCodes.find((c) => fullPhone.startsWith(c.code));
        if (found) {
          countryCode = found.code;
          phoneNumber = fullPhone.substring(found.code.length);
        } else {

          // Fallback regex split: + followed by 1-4 digits
          const match = fullPhone.match(/^(\+\d{1,4})(.*)$/);
          if (match) {
            countryCode = match[1];
            phoneNumber = match[2];
          }
        }
      }

      setForm((prev) => ({
        ...prev,
        name: (data as any)?.name || "",
        email: (data as any)?.email || "",
        countryCode,
        phone: phoneNumber,
        password: "",
      }));
      setCreatePassword(false);
    }

  }, [data]);

  const cardClass =
    theme === "dark"
      ? "bg-[#1E1E1E] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const inputClassBase =
    "w-full rounded-md px-3 py-2 outline-none transition-colors";
  const inputClass =
    theme === "dark"
      ? `${inputClassBase} bg-[#111111] border border-[#2C2C2C] placeholder-gray-500 focus:border-[#B6CB24]`
      : `${inputClassBase} bg-white border border-gray-300 placeholder-gray-500 focus:border-[#B6CB24]`;

  const labelClass = theme === "dark" ? "text-gray-300" : "text-gray-700";

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    // sanitize phone to digits only
    if (name === "phone") {
      const cleaned = value.replace(/\D/g, "");
      setForm((f) => ({ ...f, phone: cleaned }));
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
    if (name === "email" && errors.email) {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
    if (name === "password" && errors.password) {
      setErrors((prev) => ({ ...prev, password: undefined }));
    }
  };

  const handleEmailBlur = () => {
    const email = form.email.trim();
    setErrors((prev) => ({
      ...prev,
      email: email && !emailRegex.test(email) ? "Email is invalid" : undefined,
    }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      return;
    }

    if (createPassword) {
      if (!form.password || form.password.trim().length < 6) {
        setErrors((prev) => ({
          ...prev,
          password: "Password is required (min 6 chars)",
        }));
        return;
      }
    }

    const payload: {
      name: string;
      email: string;
      phone: string;
      password?: string;
    } = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: `${form.countryCode}${form.phone.trim()}`,
    };

    if (createPassword) payload.password = form.password?.trim();

    try {
      await editAdmin({ id: id ?? "", data: payload }).unwrap();
      navigate(adminRoutes.adminList);
    } catch (e) {
      console.error("Edit admin failed:", e);
    }
  };

  // sidebar offset
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  if (isLoading) return <div>Loading...</div>;
  if (isError || !data) return <div>Failed to load admin.</div>;

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50"
        }`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      <div className="mb-4">
        <h1
          className={`text-xl font-semibold ${theme === "dark" ? "text-gray-100" : "text-gray-900"
            }`}
        >
          Edit Admin
        </h1>
        <div className="text-sm mt-1">
          <Link
            to="/admin"
            className={theme === "dark" ? "text-gray-400" : "text-gray-500"}
          >
            All Admins
          </Link>
          <span
            className={theme === "dark" ? "text-gray-600" : "text-gray-400"}
          >
            {" "}
            &nbsp;&gt;&nbsp;{" "}
          </span>
          <span className="text-[#B6CB24] font-medium">Edit Admin</span>
        </div>
      </div>

      {/* Card */}
      <form
        onSubmit={onSubmit}
        className={`${cardClass} rounded-lg p-4 md:p-6`}
      >
        <div className="mb-6">
          <h2
            className={`text-base font-medium border-b pb-2 border-gray-700 ${theme === "dark" ? "text-white" : "text-gray-800"
              }`}
          >
            Edit Admin
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>Name</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter here"
              className={inputClass}
            />
          </div>
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>Email</label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              placeholder="Enter here"
              className={`${inputClass} ${errors.email ? "border border-red-500" : ""
                }`}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Phone Number
            </label>
            <CountryPhoneInput
              countryCode={form.countryCode}
              phoneNumber={form.phone}
              onCountryCodeChange={(code) => setForm((f) => ({ ...f, countryCode: code }))}
              onPhoneNumberChange={handleChange}
              error={errors.phone}
              placeholder="Enter here"
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}

          </div>
        </div>

        {/* Password Toggle and Field */}
        <div className="mt-4 flex items-center gap-3">
          <span className={`text-sm ${labelClass}`}>Change Password?</span>
          <button
            type="button"
            onClick={() => setCreatePassword((prev) => !prev)}
            aria-pressed={createPassword}
            className={`relative inline-flex items-center h-6 w-12 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${createPassword
              ? "bg-[#B6CB24]"
              : theme === "dark"
                ? "bg-[#2C2C2C]"
                : "bg-gray-300"
              }`}
          >
            <span
              className={`inline-block w-5 h-5 bg-white rounded-full transform transition-transform ${createPassword ? "translate-x-6" : "translate-x-1"
                }`}
            />
          </button>
        </div>
        {createPassword && (
          <div className="mt-2 md:w-1/3">
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Password
            </label>
            <div className="relative">
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={handleChange}
                placeholder="Enter new password"
                className={`${inputClass} ${errors.password ? "border border-red-500" : ""
                  }`}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500"
                tabIndex={-1}
              >
                {showPassword ? (
                  <FaEyeSlash className="w-5 h-5" />
                ) : (
                  <FaEye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password}</p>
            )}
          </div>
        )}

        {/* Divider */}
        <div
          className={`mt-4 h-px ${theme === "dark" ? "bg-[#2C2C2C]" : "bg-gray-200"
            }`}
        />

        {/* Actions */}
        <div className="mt-4 flex items-center gap-3">
          <button
            type="submit"
            disabled={isUpdating}
            className="px-8 py-1.5 rounded-md bg-[#B6CB24] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background:
                "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
            }}
          >
            {isUpdating ? "Saving..." : "Update"}
          </button>
          <Link
            to={adminRoutes.adminList}
            className={`px-8 py-1.5 rounded-md font-medium ${theme === "dark"
              ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
          >
            Not Now
          </Link>
        </div>
      </form>
    </div>
  );
};

export default AdminEdit;
